import {useCallback, useEffect, useState, useRef} from 'react'
import {handleIframeSearchParamsUpdate, IframeMessage} from '~/routes/translate/helpers/utils'
import {AppParams} from '~/utils/url'
import {useRootLoaderData} from '~/utils/useRootLoaderData'

export const useLaraIframe = ({
  appSearchParams,
  isBot
}: {
  appSearchParams: AppParams
  isBot: boolean
}) => {
  const [isLoadingIframe, setIsLoadingIframe] = useState(true)
  const [everythingSettled, setEverythingSettled] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const iframeRef = useRef<HTMLIFrameElement | null>(null)

  const {pathData, env} = useRootLoaderData()

  const iframeBaseUrl = env?.laraAppUrl
  const langPair = pathData?.langPair
  const appSearch = Object.fromEntries(
    Object.entries(appSearchParams).filter(([, value]) => value !== null)
  )

  const queryParams = new URLSearchParams({
    isLandingPreview: 'true',
    lng: pathData?.language || 'en',
    ...appSearch,
    ...(langPair && {
      source: langPair.source,
      target: langPair.target
    })
  }).toString()

  const finalPath = `${iframeBaseUrl}/translate?${queryParams}`

  const iframeRefCallback = useCallback(
    (iframe: HTMLIFrameElement | null) => {
      if (!iframe || isBot) return

      iframeRef.current = iframe
      iframe.src = finalPath
    },
    [finalPath, isBot]
  )

  const handleIframeMessage = useCallback(
    (event: IframeMessage) => {
      if (!event.data?.action || event.origin !== iframeBaseUrl) return

      switch (event.data.action) {
        case 'checkOrigin':
          event.source?.postMessage({action: 'originConfirmed'}, {targetOrigin: iframeBaseUrl})
          break
        case 'previewReady':
          setIsLoadingIframe(false)
          setTimeout(() => {
            setEverythingSettled(true)
          }, 1000)
          break
        case 'openDialog':
          setIsDialogOpen(true)
          break
        case 'closeDialog':
          setIsDialogOpen(false)
          break
        case 'updateSearchParams':
          handleIframeSearchParamsUpdate(event.data.payload)
      }
    },
    [iframeBaseUrl]
  )

  // This is necessary since a normal click on the iframe will not trigger a clickOutside event
  // and the modal will not close
  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    const handleBlur = () => {
      timeoutId = setTimeout(() => {
        const activeElement = document.activeElement
        if (!activeElement || activeElement.tagName !== 'IFRAME') return

        window.postMessage({action: 'closeParentDialog'})
      }, 0)
    }

    window.addEventListener('blur', handleBlur)

    return () => {
      window.removeEventListener('blur', handleBlur)
      clearTimeout(timeoutId)
    }
  }, [])

  useEffect(() => {
    window.addEventListener('message', handleIframeMessage)

    return () => {
      window.removeEventListener('message', handleIframeMessage)
    }
  }, [handleIframeMessage])

  return {iframeRefCallback, isLoadingIframe, everythingSettled, isDialogOpen}
}
